// #region Imports
import { DialogSidePaneRequest } from "_shared/_views/dialogs/dialog";
import { useSyncExternalStore } from "react";
// #endregion

/**
 * How wide the picker's four columns and its row action are laid out, in pixels.
 *
 * 265 for the name, 120 for the kind, 190 for the counterparty, 130 for the date, 70 for the row
 * action. Under MRT's `grid` layout a declared size is a floor -- the cells do not shrink below
 * it -- so this is not a preference. A table given less than this gets a horizontal scrollbar,
 * which is the cramped surface the whole rebuild exists to get away from.
 */
export const PICKER_COLUMN_BUDGET = 775;

/** The padding the picker pane puts either side of the table. */
export const PICKER_PANE_PADDING = 32;

/** What a vertical scrollbar takes out of the pane before the columns see it. */
export const PICKER_SCROLLBAR_ALLOWANCE = 15;

/**
 * The narrowest the picker pane may be before the table starts scrolling sideways.
 *
 * The column budget, plus the pane's own padding, plus the scrollbar the rows put down the right.
 * Every breakpoint below is derived from this number rather than chosen: this is the only width in
 * the surface that content actually requires.
 */
export const PICKER_PANE_MINIMUM =
  PICKER_COLUMN_BUDGET + PICKER_PANE_PADDING + PICKER_SCROLLBAR_ALLOWANCE;

/**
 * What the picker pane is given when there is room to spare.
 *
 * 38px above the minimum, which is what the name column spends on the "Already on this team" chip
 * before it starts truncating the name beside it.
 */
export const PICKER_PANE_PREFERRED = 860;

/**
 * The profile's width beside the picker, in pixels.
 *
 * Measured rather than picked: at 516px the header carries the avatar, the whole of a two-part
 * name, and the profile-type chip under it, which is what a reader needs in order to know who they
 * are staffing. Under about 500 the name starts truncating and the profile stops being worth the
 * room it takes -- which is the width at which it becomes a rail instead.
 */
export const PROFILE_PANE_WIDTH = 516;

/**
 * The context rail: the profile reduced to who it is.
 *
 * Avatar, name and company, and nothing else. 180px is the smallest width that fits a 56px avatar
 * beside nothing, a name on two lines under it, and a company line under that, with the rail's own
 * padding either side.
 */
export const PROFILE_RAIL_WIDTH = 180;

/**
 * The picker reduced to the control that brings it back, for when the reader expands the profile
 * over it.
 *
 * A strip rather than nothing at all, because a picker that vanished when the profile expanded
 * would leave the reader with no way back to the search they had typed.
 */
export const PICKER_RAIL_WIDTH = 48;

/**
 * What MUI's dialog paper keeps clear of the window: 32px of margin on each side.
 *
 * Every viewport threshold below is a pane budget plus this, because this is what separates the
 * window the media query measures from the paper the panes are laid out in.
 */
export const DIALOG_PAPER_MARGIN = 64;

/**
 * The window width at or above which the profile and the picker both fit at their full widths.
 *
 * `516 + 822 + 64 = 1402`. Below this, one of the two has to give, and the profile is the one that
 * can be said in less room.
 */
export const WIDE_FROM = PROFILE_PANE_WIDTH + PICKER_PANE_MINIMUM + DIALOG_PAPER_MARGIN;

/**
 * The window width at or above which a context rail still fits beside the picker.
 *
 * `180 + 822 + 64 = 1066`. Below this the rail is taking room the table needs, so the rail goes
 * and the table has the dialog to itself.
 */
export const MEDIUM_FROM = PROFILE_RAIL_WIDTH + PICKER_PANE_MINIMUM + DIALOG_PAPER_MARGIN;

/**
 * The window width below which nothing can be done about the table's own width.
 *
 * `822 + 64 = 886`. Under this the picker pane is narrower than the columns, so MRT gives the table
 * a horizontal scrollbar of its own -- the table scrolls, the dialog does not -- and the reader can
 * put columns away from the toolbar to get out of it. Stated here so that the narrow stage is not
 * read as a promise it cannot keep.
 */
export const PICKER_SCROLL_BELOW = PICKER_PANE_MINIMUM + DIALOG_PAPER_MARGIN;

/** Which of the three arrangements the window has room for. */
export type StaffingPickerStage = "wide" | "medium" | "narrow";

/** How the dialog is divided between the profile and the picker. */
export type StaffingPickerLayout = {
  /** Which arrangement the window has room for. */
  stage: StaffingPickerStage;
  /** How much of the person is on screen beside the picker. */
  profile: "full" | "rail" | "hidden";
  /** Whether the picker is the table itself or the strip that brings it back. */
  picker: "table" | "rail";
  /** What the dialog asks to be, in pixels, while the picker is open. */
  dialogWidth: number;
};

/**
 * Which arrangement a window of this width has room for.
 *
 * A window width rather than a measurement of the dialog, because the dialog's width is derived
 * from this and not the other way round: asking the dialog how wide it is in order to decide how
 * wide to make it is the loop the first version of this surface got stuck in.
 */
export function staffingPickerStage(viewportWidth: number): StaffingPickerStage {
  if (viewportWidth >= WIDE_FROM) return "wide";
  if (viewportWidth >= MEDIUM_FROM) return "medium";
  return "narrow";
}

/**
 * How the dialog is divided, from the window's width and whether the reader has asked for the
 * profile back.
 *
 * **The profile is never covered.** At every width it is beside the picker, railed beside it, or
 * pushed off the screen by it -- and the reader can always have it back. The panel that slid over
 * the top of the profile is the thing this replaces.
 *
 * Expanding the profile at the two narrower stages pushes the picker off rather than reopening the
 * two-pane arrangement, because the two-pane arrangement does not fit: under 1402px the full
 * profile and a table with no horizontal scrollbar cannot both be on the screen, and the picker
 * keeps its place as a rail so the reader can come back to the search they typed.
 */
export function staffingPickerLayout(
  viewportWidth: number,
  profileExpanded: boolean,
): StaffingPickerLayout {
  const stage = staffingPickerStage(viewportWidth);
  if (stage === "wide") {
    return {
      stage,
      profile: "full",
      picker: "table",
      dialogWidth: PROFILE_PANE_WIDTH + PICKER_PANE_PREFERRED,
    };
  }

  const dialogWidth =
    stage === "medium" ? PROFILE_RAIL_WIDTH + PICKER_PANE_PREFERRED : PICKER_PANE_PREFERRED;

  if (profileExpanded) {
    return { stage, profile: "full", picker: "rail", dialogWidth };
  }

  return {
    stage,
    profile: stage === "medium" ? "rail" : "hidden",
    picker: "table",
    dialogWidth,
  };
}

/**
 * The layout, as the room the dialog is being asked to set aside.
 *
 * Whichever pane is showing its full self takes a stated width and the other takes what is left:
 * the profile is a floor at the wide stage so that the table gives up the spare pixels rather than
 * the person's name, and the picker is the flexible one everywhere else because at those widths the
 * table is what the reader came for.
 */
export function staffingSidePaneRequest(layout: StaffingPickerLayout): DialogSidePaneRequest {
  const content = (() => {
    if (layout.profile === "hidden") return "hidden" as const;
    if (layout.profile === "rail") return PROFILE_RAIL_WIDTH;
    return layout.stage === "wide" ? PROFILE_PANE_WIDTH : ("flexible" as const);
  })();

  return {
    dialogWidth: layout.dialogWidth,
    content,
    // The profile itself is drawn wherever it is drawn whole. Where it is a rail, the rail is the
    // picker's own and stands in the column the profile would have had; where it is pushed off,
    // nothing stands there at all.
    drawContent: layout.profile === "full",
    pane: layout.picker === "rail" ? PICKER_RAIL_WIDTH : ("flexible" as const),
  };
}

/** What the dialog is asked for while the picker is shut: nothing, and no room. */
export const STAFFING_PANE_SHUT: DialogSidePaneRequest = {
  dialogWidth: null,
  content: "flexible",
  drawContent: true,
  pane: 0,
};

const NO_VIEWPORT = () => 0;

function subscribeToViewport(onChange: () => void) {
  window.addEventListener("resize", onChange);
  return () => window.removeEventListener("resize", onChange);
}

function readViewportWidth() {
  return window.innerWidth;
}

/**
 * How wide the window is, kept current as the reader resizes it.
 *
 * A number off `window`, deliberately, rather than a media query or a measured element. A media
 * query cannot be asked what the answer would be at some other width, and a measured element
 * reports nothing at all under jsdom -- so either one would put the whole of this surface's
 * behaviour beyond the reach of anything but a browser. A width is a number a test can set.
 */
export function useViewportWidth(): number {
  return useSyncExternalStore(subscribeToViewport, readViewportWidth, NO_VIEWPORT);
}
